import type { ReactNode } from "react"
import React, { useContext } from "react"
import {
    Button,
    componentStyles,
    useTheme,
} from "@mallardbay/lib-react-components"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons/faChevronDown"
import { faChevronUp } from "@fortawesome/free-solid-svg-icons/faChevronUp"

import ResponsiveBlock from "~components/shared/responsive-block"
import { ResponsiveContext } from "~config/responsive-provider"
import { formatPrice } from "~utils/helpers"
import COPY from "~config/copy-constants"
import Box from "~components/shared/todo-lib-react-components/box"
import Skeleton from "~components/shared/todo-lib-react-components/skeleton"

interface Props {
    readonly total?: number
    readonly isLoading: boolean
    readonly drawerIsOpen: boolean
    readonly setDrawerIsOpen: (isOpen: boolean) => void
    readonly renderNavButtons?: () => ReactNode
}

// TODO rename to MobileTripSummarySheet
// TODO return null instead of using css to not render
export default function TripSummaryBar({
    total,
    isLoading,
    drawerIsOpen,
    setDrawerIsOpen,
    renderNavButtons,
}: Props) {
    const { windowScroll } = useContext(ResponsiveContext)
    const styles = useStyles({ isScrolledToBottom: windowScroll.isBottom })

    const amountText = total ? formatPrice(total) : COPY.SELECT_YOUR_DATES
    const buttonText = drawerIsOpen ? COPY.HIDE_DETAILS : COPY.SHOW_DETAILS
    const buttonIcon = drawerIsOpen ? faChevronDown : faChevronUp

    return (
        <Box style={styles.root}>
            <ResponsiveBlock>
                <Box style={styles.showDetailsContainer}>
                    <Button
                        style={styles.button}
                        onClick={() => setDrawerIsOpen(!drawerIsOpen)}
                    >
                        {buttonText}
                        <FontAwesomeIcon icon={buttonIcon} />
                    </Button>
                </Box>
                <Box style={styles.container}>
                    {isLoading ? (
                        <Skeleton height="24px" width="112px" />
                    ) : (
                        <Box style={styles.amount}>{amountText}</Box>
                    )}
                    {renderNavButtons?.()}
                </Box>
            </ResponsiveBlock>
        </Box>
    )
}

function useStyles({ isScrolledToBottom }: { isScrolledToBottom?: boolean }) {
    const { colors, shadows } = useTheme()

    return componentStyles({
        root: {
            zIndex: 1,
            width: "100vw",
            position: "fixed",
            bottom: 0,
            left: 0,
            transition: "0.25s ease-out",
            color: colors.text,
            display: { base: "block", md: "none" },
            borderTopRadius: "20px",
            backgroundColor: colors.bgColor,
            border: `1px solid ${colors.borderColor}`,
            boxShadow: shadows["2xl"],
            ...(isScrolledToBottom && { transform: "translateY(80px)" }),
        },
        container: {
            height: "6rem",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            borderTop: `1px solid ${colors.borderColor}`,
        },
        amount: {
            fontSize: "18px",
            fontWeight: 700,
        },
        showDetailsContainer: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "4rem",
        },
        button: {
            fontWeight: 600,
            color: "#f65e38",
            backgroundColor: "transparent",
            paddingRight: 0,
            paddingLeft: "8px",
            gap: 3,
            ":hover": {
                backgroundColor: "transparent",
            },
        },
    })
}
